import { useState } from 'react';
import { Link } from 'react-router';
import { ENV_NAME_RE, type BranchRule, type EnvironmentDTO, type ProjectDTO } from '@devrobot/shared';
import { CircleAlert, CircleCheck, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, seg } from '../../api/client';
import { qk, useApiMutation, useEnvironments } from '../../api/queries';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorAlert } from '../../components/ErrorAlert';
import { Modal } from '../../components/Modal';
import { Section } from '../../components/PageHeader';
import { SkeletonTable } from '../../components/Skeleton';
import { useToast } from '../../components/Toaster';
import { Haptic } from '../../kit/haptics';
import { haptic } from '../../kit/native';

const onMutationError = () => haptic('error');

function BranchRuleModal({ projectKey, env, onClose }: { projectKey: string; env: EnvironmentDTO; onClose: () => void }) {
  const toast = useToast();
  const [mode, setMode] = useState<BranchRule['mode']>(env.branchRule.mode);
  const [patterns, setPatterns] = useState(env.branchRule.mode === 'patterns' ? env.branchRule.patterns.join('\n') : '');
  const save = useApiMutation((rule: BranchRule) => api.patch<EnvironmentDTO>(`/api/v1/projects/${seg(projectKey)}/environments/${seg(env.name)}`, { branchRule: rule }), {
    invalidate: [qk.environments(projectKey)],
  });
  const list = patterns
    .split(/[\n,]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const rule: BranchRule = mode === 'patterns' ? { mode, patterns: list } : { mode };
  const saveDisabled = save.isPending || (mode === 'patterns' && list.length === 0);

  return (
    <Modal title={`Branch rule for ${env.name}`} onClose={onClose} size="lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (saveDisabled) {
            haptic('error');
            return;
          }
          save.mutate(rule, {
            onSuccess: () => (toast.success(`Branch rule for ${env.name} updated`, { description: 'Existing deployments are not changed.' }), onClose()),
            onError: onMutationError,
          });
        }}
      >
        <p className="text-sm text-base-content/65">Rules are stored in DevRobot, not in the repository, so a branch cannot relax its own rule. The Deploy tab and the CLI apply the same rule.</p>
        <div className="space-y-2" role="radiogroup" aria-label="Branch rule">
          {[
            { v: 'any' as const, t: 'Any branch', d: 'No restriction beyond deploy permission.' },
            { v: 'patterns' as const, t: 'Matching branch names', d: 'Exact names or patterns; * matches any characters, including /.' },
            { v: 'protected' as const, t: 'GitLab protected branches only', d: 'Only branches protected in GitLab may be deployed.' },
          ].map((o) => (
            <label
              key={o.v}
              className="dr-lift flex cursor-pointer items-start gap-3 rounded-field border border-hairline bg-base-100 p-3.5 has-checked:border-primary/45 has-checked:bg-primary/[0.04] has-checked:shadow-[var(--dr-ring)]"
            >
              <input type="radio" name="branch-rule" className="radio radio-primary radio-sm mt-0.5" checked={mode === o.v} onChange={() => setMode(o.v)} />
              <span>
                <span className="block text-sm font-semibold">{o.t}</span>
                <span className="block text-sm text-base-content/60">{o.d}</span>
              </span>
            </label>
          ))}
        </div>
        {mode === 'patterns' ? (
          <fieldset className="fieldset animate-rise">
            <legend className="fieldset-legend">Allowed branches</legend>
            <textarea className="textarea w-full font-mono" rows={4} value={patterns} onChange={(e) => setPatterns(e.target.value)} placeholder={'main\nrelease/*'} />
            <p className="label">One per line or comma separated. {list.length === 0 ? 'Add at least one.' : `${list.length} pattern${list.length === 1 ? '' : 's'}.`}</p>
          </fieldset>
        ) : null}
        <ErrorAlert error={save.error} title="The rule was not saved" />
        <div className="modal-action">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <Haptic disabled={saveDisabled}>
            <button type="submit" className="btn btn-primary" disabled={saveDisabled}>
              {save.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : null}
              Save rule
            </button>
          </Haptic>
        </div>
      </form>
    </Modal>
  );
}

export function EnvironmentsTab({ project }: { project: ProjectDTO }) {
  const key = project.key;
  const toast = useToast();
  const envs = useEnvironments(key);
  const [editingRule, setEditingRule] = useState<EnvironmentDTO | null>(null);
  const [unprotecting, setUnprotecting] = useState<EnvironmentDTO | null>(null);
  const [deleting, setDeleting] = useState<EnvironmentDTO | null>(null);
  const [newName, setNewName] = useState('');
  const [newProtected, setNewProtected] = useState(false);

  const invalidate = [qk.environments(key), qk.project(key)];
  const patch = useApiMutation((v: { env: string; body: { protected?: boolean } }) => api.patch<EnvironmentDTO>(`/api/v1/projects/${seg(key)}/environments/${seg(v.env)}`, v.body), { invalidate });
  const create = useApiMutation((body: { name: string; protected: boolean }) => api.post<EnvironmentDTO>(`/api/v1/projects/${seg(key)}/environments`, body), { invalidate });
  const remove = useApiMutation((env: string) => api.delete<{ ok: true }>(`/api/v1/projects/${seg(key)}/environments/${seg(env)}`), { invalidate });

  const canManage = project.capabilities['env.manage'];
  const nameValid = ENV_NAME_RE.test(newName);
  const createDisabled = !nameValid || create.isPending;

  // Cell renderers shared by the phone list and the table.
  const protection = (e: EnvironmentDTO) =>
    e.can.manage ? (
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          className="toggle toggle-sm toggle-warning"
          checked={e.protected}
          disabled={patch.isPending}
          onChange={(ev) => (ev.target.checked ? patch.mutate({ env: e.name, body: { protected: true } }, { onError: onMutationError }) : setUnprotecting(e))}
          aria-label={`Protected: ${e.name}`}
        />
        <span className="text-sm">{e.protected ? 'protected' : 'unprotected'}</span>
      </label>
    ) : e.protected ? (
      <span className="badge badge-warning badge-soft badge-sm gap-1">
        <Lock className="size-3" aria-hidden="true" /> protected
      </span>
    ) : (
      <span className="text-sm text-base-content/65">unprotected</span>
    );

  const branchRule = (e: EnvironmentDTO) => (
    <>
      <span className="break-anywhere">{e.branchRuleDescription}</span>
      {e.can.manage ? (
        <button type="button" className="btn btn-ghost btn-xs ml-1" onClick={() => setEditingRule(e)} aria-label={`Edit branch rule for ${e.name}`}>
          <Pencil className="size-3" aria-hidden="true" />
        </button>
      ) : null}
    </>
  );

  const configuration = (e: EnvironmentDTO) => {
    const missing = e.completeness.missing.length + e.completeness.missingFiles.length;
    return (
      <>
        <Link className="link link-hover" to={`?tab=config&env=${encodeURIComponent(e.name)}`}>
          {missing === 0 ? (
            <span className="badge badge-success badge-soft badge-sm gap-1">
              <CircleCheck className="size-3" aria-hidden="true" />
              complete
            </span>
          ) : (
            <span className="badge badge-error badge-soft badge-sm gap-1">
              <CircleAlert className="size-3" aria-hidden="true" />
              {missing} missing
            </span>
          )}
        </Link>
        <div className="text-xs text-base-content/60">
          {e.completeness.required} required · {e.completeness.total} total
          {e.completeness.empty.length ? ` · ${e.completeness.empty.length} intentionally empty` : ''}
        </div>
      </>
    );
  };

  const deleteAction = (e: EnvironmentDTO) =>
    !e.isDefault && e.can.manage ? (
      <span className={e.targetCount > 0 ? 'tooltip tooltip-left' : ''} data-tip={e.targetCount > 0 ? 'Remove its deployment targets first' : undefined}>
        <button type="button" className="btn btn-ghost btn-xs dr-ink-error" disabled={e.targetCount > 0} onClick={() => setDeleting(e)} aria-label={`Delete environment ${e.name}`}>
          <Trash2 className="size-3.5" aria-hidden="true" />
        </button>
      </span>
    ) : null;

  const envName = (e: EnvironmentDTO) => (
    <>
      <span className="font-medium">{e.name}</span>
      {e.isDefault ? <span className="badge badge-ghost badge-xs ml-2">default</span> : null}
    </>
  );

  return (
    <div className="space-y-6">
      <Section
        title="Environments"
        description="Every project has development, staging and production. Protected environments require Maintainer to deploy, operate or change configuration, and Owner to reveal secret values."
      >
        {envs.isPending ? <SkeletonTable rows={3} /> : null}
        {envs.isError ? <ErrorAlert error={envs.error} onRetry={() => void envs.refetch()} /> : null}
        <ErrorAlert error={patch.error} title="The environment was not updated" />
        {envs.data ? (
          <div className="-mx-4 border-y border-hairline sm:mx-0 sm:overflow-hidden sm:rounded-field sm:border">
            {/* Phones: stacked rows instead of a sideways-scrolling table. */}
            <ul className="divide-y divide-hairline md:hidden">
              {envs.data.map((e) => (
                <li key={e.id} className="space-y-2.5 px-4 py-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {envName(e)}
                      <p className="text-xs text-base-content/60">
                        {e.targetCount} target{e.targetCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {protection(e)}
                      {deleteAction(e)}
                    </div>
                  </div>
                  <div className="text-sm text-base-content/70">{branchRule(e)}</div>
                  <div className="text-sm">{configuration(e)}</div>
                </li>
              ))}
            </ul>
            <div className="overflow-x-auto max-md:hidden">
              <table className="table table-sm">
                <thead>
                  <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                    <th>Environment</th>
                    <th>Protection</th>
                    <th>Branch rule</th>
                    <th>Configuration</th>
                    <th>Targets</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {envs.data.map((e) => (
                    <tr key={e.id} className="hover:bg-base-200/50">
                      <td>{envName(e)}</td>
                      <td>{protection(e)}</td>
                      <td className="max-w-xs text-sm">{branchRule(e)}</td>
                      <td className="text-sm">{configuration(e)}</td>
                      <td className="text-sm tabular-nums">{e.targetCount}</td>
                      <td className="text-right">{deleteAction(e)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </Section>

      {canManage ? (
        <Section title="Add a custom environment" description="For example qa or demo. Custom environments start empty: set their configuration before deploying.">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (createDisabled) {
                haptic('error');
                return;
              }
              create.mutate(
                { name: newName, protected: newProtected },
                {
                  onSuccess: () => {
                    toast.success(`Environment ${newName} created`, { description: 'Set its configuration before deploying to it.' });
                    setNewName('');
                    setNewProtected(false);
                  },
                  onError: onMutationError,
                },
              );
            }}
          >
            <fieldset className="fieldset py-0">
              <legend className="fieldset-legend">Name</legend>
              <input className={`input w-56 font-mono ${newName && !nameValid ? 'input-error' : ''}`} value={newName} onChange={(e) => setNewName(e.target.value.toLowerCase())} placeholder="qa" />
              <p className="label">Lowercase letters, digits and dashes.</p>
            </fieldset>
            <label className="mb-7 flex cursor-pointer items-center gap-2">
              <input type="checkbox" className="checkbox checkbox-sm" checked={newProtected} onChange={(e) => setNewProtected(e.target.checked)} />
              Protected
            </label>
            <Haptic disabled={createDisabled} className="mb-6">
              <button type="submit" className="btn" disabled={createDisabled}>
                {create.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
                Add environment
              </button>
            </Haptic>
          </form>
          <ErrorAlert error={create.error} title="The environment was not created" />
        </Section>
      ) : null}

      {editingRule ? <BranchRuleModal projectKey={key} env={editingRule} onClose={() => setEditingRule(null)} /> : null}
      {unprotecting ? (
        <ConfirmDialog
          title={`Remove protection from ${unprotecting.name}?`}
          confirmLabel="Make unprotected"
          tone="warning"
          busy={patch.isPending}
          error={patch.error}
          onCancel={() => (setUnprotecting(null), patch.reset())}
          onConfirm={() =>
            patch.mutate(
              { env: unprotecting.name, body: { protected: false } },
              { onSuccess: () => (toast.success(`${unprotecting.name} is now unprotected`, { description: 'Running applications are not affected.' }), setUnprotecting(null)), onError: onMutationError },
            )
          }
        >
          <p>Developers will be able to deploy to, operate and change the configuration of {unprotecting.name}, and reveal its secret values. Running applications are not affected.</p>
        </ConfirmDialog>
      ) : null}
      {deleting ? (
        <ConfirmDialog
          title={`Delete environment ${deleting.name}?`}
          confirmLabel="Delete environment"
          tone="error"
          typedConfirmation={deleting.name}
          busy={remove.isPending}
          error={remove.error}
          onCancel={() => (setDeleting(null), remove.reset())}
          onConfirm={() =>
            remove.mutate(deleting.name, {
              onSuccess: () => (toast.success(`Environment ${deleting.name} deleted`, { description: 'Its configuration, secrets and private files were removed.' }), setDeleting(null)),
              onError: onMutationError,
            })
          }
        >
          <p>
            This permanently deletes the environment together with <strong>all of its configuration values, secrets and private files</strong>. It has no deployment targets, so no running application is affected.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
